import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useAppBootstrap } from '../context/AppBootstrapContext';
import { Logo } from '../components/Logo';
import { Shield, Zap, Layers, Clock } from 'lucide-react';
import { BRAND_NAME } from '../brand';

const WelcomePage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { session, loading } = useAuth();
    const { bootstrap, loading: bootstrapLoading } = useAppBootstrap();

    useEffect(() => {
        if (loading || bootstrapLoading) return;
        if (session && bootstrap) {
            const target = bootstrap.needs_onboarding ? '/onboarding' : '/app/today';
            navigate({ pathname: target, search: location.search }, { replace: true });
        }
    }, [session, bootstrap, loading, bootstrapLoading, navigate, location.search]);

    return (
        <div
            data-testid="welcome-page"
            className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100 flex items-center justify-center px-4 py-12"
        >
            {/* Subtle gradient orb background */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-1/4 -left-32 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
                <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
            </div>

            <div className="max-w-3xl w-full space-y-8 relative z-10">
                {/* Logo + Wordmark */}
                <div className="flex items-center gap-3">
                    <Logo variant="wordmark" size={24} appearance="dark" />
                </div>

                <div className="space-y-4">
                    <h1 className="text-4xl sm:text-5xl font-bold leading-tight">
                        <span className="bg-gradient-to-r from-white via-slate-100 to-slate-300 bg-clip-text text-transparent">
                            Your life, organized.
                        </span>
                    </h1>
                    <p className="text-slate-400 text-base sm:text-lg max-w-xl leading-relaxed">
                        Job Search, Family, and the rest of life.
                        Secure by default. Connect Gmail when you&apos;re ready. If the connection stops, {BRAND_NAME} tells you — it does not pretend your mail is fine.
                    </p>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                    <Feature
                        icon={<Shield className="w-4 h-4" />}
                        title="OAuth-only"
                        desc="Sign in with Google. We don't keep your password."
                    />
                    <Feature
                        icon={<Layers className="w-4 h-4" />}
                        title="Scoped Gmail"
                        desc="Read-only access. Mailbox connect is separate consent."
                    />
                    <Feature
                        icon={<Zap className="w-4 h-4" />}
                        title="Smart Modes"
                        desc="Life areas like Job Search and Family — switch focus when you need to."
                    />
                    <Feature
                        icon={<Clock className="w-4 h-4" />}
                        title="Setup"
                        desc={`Pick a life area, connect Gmail, and start a sync. If mail doesn't show, ${BRAND_NAME} says so.`}
                    />
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    <button
                        data-testid="welcome-get-started"
                        onClick={() => navigate('/login')}
                        className="px-6 py-3.5 rounded-xl bg-gradient-to-r from-teal-500 to-teal-400 text-slate-900 font-semibold shadow-lg shadow-teal-500/25 hover:shadow-xl hover:shadow-teal-500/30 hover:scale-[1.02] transition-all duration-200"
                    >
                        Get Started
                    </button>
                    <button
                        data-testid="welcome-learn-more"
                        onClick={() => navigate('/learn-more')}
                        className="px-6 py-3.5 rounded-xl border border-white/10 text-slate-100 hover:bg-white/5 hover:border-white/20 transition-all duration-200"
                    >
                        Learn More
                    </button>
                </div>

                <p className="text-xs text-slate-500 pt-4">
                    Sign-in and Gmail connect are separate steps — you control what we access.
                </p>
            </div>
        </div>
    );
};

const Feature = ({ icon, title, desc }: { icon: React.ReactNode; title: string; desc: string }) => (
    <div className="group rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm px-4 py-4 hover:bg-white/10 hover:border-white/20 transition-all duration-200">
        <div className="flex items-center gap-2 mb-1">
            <span className="text-teal-400">{icon}</span>
            <span className="text-sm font-semibold text-slate-100">{title}</span>
        </div>
        <div className="text-xs text-slate-400 leading-relaxed">{desc}</div>
    </div>
);

export default WelcomePage;
